import { useCallback, useEffect, useRef, useState } from "react";
import { useCompactWidgetDrag } from "./use-compact-widget-drag";
import { cn } from "@/lib/cn";
import { OPEN_COMPACT_HISTORY_EVENT } from "@/lib/compact-history-event";
import { useDesktopUpdateStore } from "./desktop-update-store";
import { CompactDenkerWidget } from "./compact-denker-widget";
import { useCursorControl } from "./use-cursor-control";
import {
  COMPACT_WIDGET_SHELL_WIDTH,
  COMPACT_WIDGET_UPDATE_SHELL_WIDTH,
} from "./compact-widget-geometry";
import { useCompactVoiceStatus } from "./use-compact-voice-status";
import { useCompactResourceOffset } from "./use-compact-resource-offset";

const UPDATE_LOGO_PULSE_MS = 2400;
const COMPACT_COLLAPSE_DELAY_MS = 350;

export function CompactWidgets() {
  const cursorControl = useCursorControl();
  const controlling = Boolean(
    cursorControl && cursorControl.phase !== "confirm",
  );
  const voice = useCompactVoiceStatus();
  const readyUpdateVersion = useDesktopUpdateStore((s) => s.readyVersion);
  const shellWidth = readyUpdateVersion
    ? COMPACT_WIDGET_UPDATE_SHELL_WIDTH
    : COMPACT_WIDGET_SHELL_WIDTH;
  const { offsetX, top, viewportHeight, isDragging, dragHandlers } =
    useCompactWidgetDrag(
      voice.showLabel ? Math.max(shellWidth, 234) : shellWidth,
    );
  const expandUp = top + 18 > viewportHeight / 2;
  const panelSpace = Math.max(
    0,
    expandUp ? top - 8 : viewportHeight - top - 44,
  );
  const [rowHover, setRowHover] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const clusterRef = useRef<HTMLDivElement>(null);
  useCompactResourceOffset(clusterRef);
  const [forceCollapsed, setForceCollapsed] = useState(false);
  const [pulsingUpdateVersion, setPulsingUpdateVersion] = useState<
    string | null
  >(null);
  const lastPulsedUpdateVersionRef = useRef<string | null>(null);
  const updatePulseTimerRef = useRef<number | null>(null);
  const collapseTimerRef = useRef<number | null>(null);
  const expanded =
    rowHover &&
    !forceCollapsed &&
    !isDragging &&
    (!voice.showLabel || voice.live || voice.phase === "speaking");
  const clearUpdatePulseTimer = useCallback(() => {
    if (updatePulseTimerRef.current === null) return;
    window.clearTimeout(updatePulseTimerRef.current);
    updatePulseTimerRef.current = null;
  }, []);
  const clearCollapseTimer = useCallback(() => {
    if (collapseTimerRef.current === null) return;
    window.clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = null;
  }, []);
  const requestCollapse = useCallback(
    (options?: { delayed?: boolean }) => {
      clearCollapseTimer();
      if (!options?.delayed) {
        setForceCollapsed(true);
        return;
      }
      collapseTimerRef.current = window.setTimeout(() => {
        setRowHover(false);
        setForceCollapsed(false);
        collapseTimerRef.current = null;
      }, COMPACT_COLLAPSE_DELAY_MS);
    },
    [clearCollapseTimer],
  );

  useEffect(() => () => clearCollapseTimer(), [clearCollapseTimer]);

  useEffect(() => {
    if (!rowHover) return;
    const resetHover = () => {
      clearCollapseTimer();
      setRowHover(false);
      setForceCollapsed(false);
    };
    const recoverPointerLeave = (event: PointerEvent) => {
      if (isDragging) return;
      if (
        event.target instanceof Node &&
        railRef.current?.contains(event.target)
      ) {
        clearCollapseTimer();
      } else if (collapseTimerRef.current === null) {
        requestCollapse({ delayed: true });
      }
    };
    window.addEventListener("blur", resetHover);
    window.addEventListener("pointermove", recoverPointerLeave);
    return () => {
      window.removeEventListener("blur", resetHover);
      window.removeEventListener("pointermove", recoverPointerLeave);
    };
  }, [clearCollapseTimer, isDragging, requestCollapse, rowHover]);

  useEffect(() => {
    const openHistory = () => {
      clearCollapseTimer();
      setForceCollapsed(false);
      setRowHover(true);
    };
    window.addEventListener(OPEN_COMPACT_HISTORY_EVENT, openHistory);
    return () =>
      window.removeEventListener(OPEN_COMPACT_HISTORY_EVENT, openHistory);
  }, [clearCollapseTimer]);

  useEffect(() => {
    if (!readyUpdateVersion) {
      lastPulsedUpdateVersionRef.current = null;
      setPulsingUpdateVersion(null);
      clearUpdatePulseTimer();
      return;
    }
    if (rowHover) {
      if (rowHover) lastPulsedUpdateVersionRef.current = readyUpdateVersion;
      setPulsingUpdateVersion(null);
      clearUpdatePulseTimer();
      return;
    }
    if (lastPulsedUpdateVersionRef.current === readyUpdateVersion) return;

    lastPulsedUpdateVersionRef.current = readyUpdateVersion;
    setPulsingUpdateVersion(readyUpdateVersion);
    clearUpdatePulseTimer();
    updatePulseTimerRef.current = window.setTimeout(() => {
      setPulsingUpdateVersion((current) =>
        current === readyUpdateVersion ? null : current,
      );
      updatePulseTimerRef.current = null;
    }, UPDATE_LOGO_PULSE_MS);
    return clearUpdatePulseTimer;
  }, [clearUpdatePulseTimer, readyUpdateVersion, rowHover]);

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-x-0 top-0",
        controlling ? "z-[32010]" : "z-50",
      )}
    >
      <div
        ref={railRef}
        className={cn(
          "pointer-events-auto absolute flex -translate-x-1/2 touch-none select-none items-start transition-[opacity,transform] duration-200 ease-out",
          isDragging ? "cursor-grabbing" : "cursor-grab",
        )}
        data-testid="desktop-compact-switcher-rail"
        style={{ left: `calc(50% + ${offsetX}px)`, top }}
        onPointerEnter={() => {
          clearCollapseTimer();
          setRowHover(true);
          setForceCollapsed(false);
          if (readyUpdateVersion)
            lastPulsedUpdateVersionRef.current = readyUpdateVersion;
          setPulsingUpdateVersion(null);
          clearUpdatePulseTimer();
        }}
        onPointerLeave={() => {
          setPulsingUpdateVersion(null);
          clearUpdatePulseTimer();
          requestCollapse({ delayed: true });
        }}
        {...dragHandlers}
      >
        <div
          ref={clusterRef}
          className="relative flex shrink-0 items-start rounded-[24px]"
          data-testid="desktop-compact-widget-cluster"
        >
          {pulsingUpdateVersion && (
            <span
              aria-hidden="true"
              className="denker-logo-update-pulse"
              data-testid="desktop-update-ready-pulse"
            />
          )}
          <CompactDenkerWidget
            expanded={expanded}
            expandUp={expandUp}
            panelSpace={panelSpace}
            dragging={isDragging}
            docked={top === 8}
            onRequestCollapse={requestCollapse}
            voice={voice}
          />
        </div>
      </div>
    </div>
  );
}
